import React from 'react';
import BaseTopicLayout from './BaseTopicLayout.js'; // Ensure the path is correct

/**
 * PrivacyPolicyPage Component.
 * Hosts the content of the Privacy Policy within the common topic layout.
 *
 * @param {function} [props.setHasAcceptedTrackingCookies=()=>{}] - Setter for the global tracking cookies state.
 * @param {boolean} [props.hasAcceptedTrackingCookies=true] - Global tracking cookies state.
 */
function PrivacyPolicyPage({ 
    setHasAcceptedTrackingCookies = () => {}, 
    hasAcceptedTrackingCookies = true 
}) {
    // The Privacy Policy content as a string.
    // The text has been modified to address the visitor directly.
    const privacyPolicyContent = `
        This Privacy Policy describes how the website https://zapdesign.top/ (hereinafter, the "Site") collects, uses, and discloses user information when you visit or interact with the Site. Your privacy is important to us, and we are committed to protecting personal data in accordance with the European Union's General Data Protection Regulation (GDPR) and applicable Italian laws.

        Data Controller
        The data controller for this Site is:
        Paolo Zappalà
        Città di Castello (PG) - Italy        
        paolo@zapdesign.top

        Data Collected and Purpose of Processing
        Various types of information are collected for various purposes, in order to provide and improve the Service offered by the Site.

        1. Navigation Data
        Type of data: IP address, browser type, operating system, referral URL, pages visited, time spent on the Site, navigation path.
        How collected: This data is automatically collected during the visit to the Site through cookies (see Cookie Policy) and similar technologies, including any traffic analysis services such as Google Analytics.
        Purpose: Web traffic analysis, improvement of site usability, performance monitoring, and security. This data is generally collected in aggregate and anonymous form.
        Legal Basis: Legitimate interest (for the operation and optimization of the Site).

        2. Data Voluntarily Provided by the User
        Type of data: Name, surname, email address, phone number, message, and any other information the user chooses to provide via the contact form or other direct interactions.
        How collected: Directly from the user via the ContactForm2.js contact form or any other form of communication (e.g., direct emails).
        Purpose: To respond to user requests, provide support, send communications related to requested services, manage collaboration requests or job opportunities.
        Legal Basis: Performance of a contract or pre-contractual measures (if the user's request is aimed at an agreement) or consent (if the user's request is general in nature and consent is provided).

        3. Data Collected via Third-Party Services
        The Site incorporates third-party services that may collect their own data. The collection and use of this data are governed by the privacy policies of the respective third parties.

        YouTube (Google LLC): For embedding videos. Video playback is subject to the user's consent to the use of tracking cookies. For more information, please consult Google's Privacy Policy.
        SoundCloud (SoundCloud Limited): For embedding audio tracks. The use of this service implies its own privacy policy. For more information, please consult SoundCloud's Privacy Policy.
        GitHub (GitHub, Inc.): If referring to repositories or navigating via external links to GitHub, their privacy policy applies.
        Render (Render Inc.): For hosting web applications (e.g., Lissajous Patterns Generator, 808 Drum Machine). For more information, please consult Render's Privacy Policy.
        AcusMagic (https://acusmagic.com/): The Site refers to this entity. Its privacy policy applies to data collected on that domain.

        Methods of Data Processing
        Data processing is carried out by means of operations or complex of operations indicated in Art. 4 No. 2) of the GDPR, namely: collection, recording, organization, storage, consultation, elaboration, modification, selection, extraction, comparison, use, interconnection, blocking, communication, cancellation, and destruction of data.
        Your personal data is processed using automated tools for the time strictly necessary to achieve the purposes for which it was collected.

        Communication and Dissemination of Data
        Your personal data will not be disclosed to unauthorized third parties. It may be communicated to third-party service providers acting on our behalf (e.g., hosting providers, analytics services, email platforms), who act as Data Processors and are bound by specific contractual agreements to ensure the security and confidentiality of the data.

        Data may only be disclosed in fulfillment of legal obligations or for the exercise of a right in court.

        Transfer of Data Abroad
        The processing of personal data takes place at the Data Controller's premises and in any other place where the parties involved in the processing are located. Some third-party services may have servers located outside the European Union. In such cases, data transfer takes place in accordance with the GDPR (e.g., standard contractual clauses, Privacy Shield if applicable, etc.).

        Data Retention
        Data collected via the contact form is retained for the time strictly necessary to manage the user's request and, subsequently, for a period not exceeding [SPECIFY A PERIOD, e.g., 12 months] for administrative or archiving purposes, unless a longer retention period is required by law or for the defense of a right in court.

        Data collected via persistent cookies has a defined duration as specified in the Cookie Policy.

        Your Rights
        As a data subject, you have the following rights under the GDPR:

        1. Right of access (Art. 15 GDPR): To obtain confirmation as to whether or not personal data concerning you is being processed, and, where that is the case, access to the personal data.
        2. Right to rectification (Art. 16 GDPR): To obtain without undue delay the rectification of inaccurate personal data concerning you.
        3. Right to erasure (Art. 17 GDPR - "Right to be forgotten"): To obtain the erasure of personal data concerning you without undue delay.
        4. Right to restriction of processing (Art. 18 GDPR): To obtain restriction of processing where certain conditions apply.
        5. Right to data portability (Art. 20 GDPR): To receive the personal data concerning you, which you have provided to a controller, in a structured, commonly used and machine-readable format and have the right to transmit those data to another controller without hindrance.
        6. Right to object (Art. 21 GDPR): To object at any time to processing of personal data concerning you.
        7. Right to withdraw consent (Art. 7 para. 3 GDPR): To withdraw consent at any time, without affecting the lawfulness of processing based on consent before its withdrawal.
        8. Right to lodge a complaint with a supervisory authority (Art. 77 GDPR): To lodge a complaint with the Garante per la protezione dei dati personali (Italian Data Protection Authority) if you believe that the processing of your data violates the GDPR.

        To exercise your rights, you can contact us at the email address provided at the beginning of this Privacy Policy.

        Changes to the Privacy Policy
        We reserve the right to make changes to this Privacy Policy at any time by publicizing them to users on this page. Users are invited to consult this page regularly, referring to the "last modified" date indicated at the top.
    `.trim(); // Removes any extra spaces at the beginning/end

    return (
        <BaseTopicLayout
            title="Privacy Policy"
            descriptionContent="Information on how your personal data is processed."
            setHasAcceptedTrackingCookies={setHasAcceptedTrackingCookies}
            hasAcceptedTrackingCookies={hasAcceptedTrackingCookies}
        >
            <div style={{ 
                padding: '20px', 
                backgroundColor: 'rgba(0, 0, 0, 0.7)', 
                borderRadius: '8px', 
                color: '#e0e0e0',
                lineHeight: '1.6',
                maxWidth: '800px',
                margin: '20px auto',
                fontSize: '0.95em'
            }}>
                {/* Splits the text into paragraphs for better formatting */}
                {privacyPolicyContent.split('\n\n').map((paragraph, index) => (
                    <p key={index} style={{ marginBottom: '1em' }}>
                        {paragraph.split('\n').map((line, lineIndex) => (
                            <React.Fragment key={lineIndex}>
                                {line}
                                {lineIndex < paragraph.split('\n').length - 1 && <br />}
                            </React.Fragment>
                        ))}
                    </p>
                ))}
            </div>
            {/* Styles for policy pages. Consider moving them to a dedicated CSS file if they become numerous. */}
            <style jsx="true">{`
                .elementor-location-footer {
                    display: none; /* Hides the footer if you don't want it on these pages */
                }
                .main-content-container, body {
                    background-color: #111111 !important; /* Dark background for the site theme */
                }
            `}</style>
        </BaseTopicLayout>
    );
}

export default PrivacyPolicyPage;
